import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import appConfig from '../../../config/appConfig';
import { useSignupReferral } from '../hooks/useSignupReferral';
import SignupBrandLogos from './SignupBrandLogos';

const signupEmailSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  organization_name: Yup.string().required('Organization name is required'),
  phone_number: Yup.string().required('Phone number is required'),
  contact_email: Yup.string().email('Invalid contact email format').required('Contact email is required'),
  termsAndCondition: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
});

const SignupEmailForm = ({ initialEmail }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signupPath, referredBy } = useSignupReferral();

  const schema = useMemo(() => signupEmailSchema, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: initialEmail || '',
      termsAndCondition: false,
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/signup', {
        ...data,
        ...(referredBy ? { referred_by: referredBy } : {}),
      })
      .then((res) => {
        dispatch(
          showSnackbar({
            message:
              res.data.message ||
              'Thank you for signing up! Our team will get back to you shortly.',
            severity: 'success',
          }),
        );
        navigate(signupPath, {
          replace: true,
          state: {
            stage: 'waiting',
            email: data.email,
            submittedAt: res.data.submittedAt || new Date().toISOString(),
          },
        });
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.errors?.message ?? 'Something went wrong.',
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <SignupBrandLogos />
      <Stack spacing={2}>
        <CustomTextInput name="email" label="Account Email" control={control} errors={errors} />
        <CustomTextInput name="name" label="Full Name" control={control} errors={errors} />
        <CustomTextInput
          name="organization_name"
          label="Organization Name"
          control={control}
          errors={errors}
        />
        <CustomTextInput name="phone_number" label="Phone Number" control={control} errors={errors} />
        <CustomTextInput name="contact_email" label="Contact Email" control={control} errors={errors} />
        <Controller
          name="termsAndCondition"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  color="primary"
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                />
              }
              label={
                <Box>
                  I agree to the{' '}
                  <a
                    href="https://marmin.ai/terms-of-use/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    terms & conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://marmin.ai/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    privacy policy
                  </a>{' '}
                  of {appConfig.brandName} platform.
                </Box>
              }
            />
          )}
        />
        {errors.termsAndCondition && (
          <FormHelperText error>{get(errors, 'termsAndCondition.message', '')}</FormHelperText>
        )}
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)} disabled={loading}>
          {loading ? 'Requesting...' : 'Request Account Activation'}
        </Button>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Already have an account?{' '}
          <Link to="/login" style={{ textDecoration: 'none', fontWeight: 400 }}>
            Sign In
          </Link>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Facing Issues?{' '}
          <a href={`mailto:${appConfig.brandEmail}`} style={{ textDecoration: 'none', fontWeight: 400 }}>
            Contact Support
          </a>
        </Typography>
      </Stack>
    </>
  );
};

export default SignupEmailForm;
